import { computed } from "vue";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import type {
  AdminOrganization,
  NetworkOrganizationWithCounts,
} from "@/api/model";

type CurrentOrganization = AdminOrganization | NetworkOrganizationWithCounts;

function getOrganizationTimezone(org: CurrentOrganization | null): string {
  if (!org) return "UTC";

  if ("timezone" in org && typeof org.timezone === "string") {
    return org.timezone;
  }

  if ("organization_preferences" in org) {
    const timezone = org.organization_preferences?.timezone;
    if (timezone && typeof timezone === "string") {
      return timezone;
    }
  }

  return "UTC";
}

export function useCurrentOrganization() {
  const authStore = useAuthStore();
  const impersonationStore = useImpersonationStore();

  const currentOrganizationId = computed(() => {
    if (
      impersonationStore.isImpersonating &&
      impersonationStore.impersonatedOrg?.id
    ) {
      return impersonationStore.impersonatedOrg.id;
    }

    return authStore.defaultOrg?.id || null;
  });

  const currentOrganization = computed(() => {
    if (
      impersonationStore.isImpersonating &&
      impersonationStore.impersonatedOrg
    ) {
      return impersonationStore.impersonatedOrg;
    }

    return authStore.defaultOrg || null;
  });

  const currentOrganizationTimezone = computed(() =>
    getOrganizationTimezone(currentOrganization.value)
  );

  return {
    currentOrganizationId,
    currentOrganization,
    currentOrganizationTimezone,
    isImpersonating: computed(() => impersonationStore.isImpersonating),
  };
}
